import React, { useEffect, useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'

import { useKeenSlider } from 'keen-slider/react'
import { DottedPagination, NumberedPaginationShort } from '@aok/ui-elements'

const MOBILE_BREAKPOINT = 600

const Slider = ({
  children,
  current,
  teaserCount,
  sliderSettings,
  sliderClassName,
  slideClassName,
  paginationClasses,
  paginationOuterClasses,
  paginationSettings,
  changeCallback,
  resizeCallback,
  onSliderCreated,
  allowPaginationOffset,
}) => {
  const slideElementRefs = useRef([])
  const initialSlideChange = useRef(true)
  const [paginationOffset, setPaginationOffset] = useState(1)
  const [paginationOffsetModifier, setPaginationOffsetModifier] = useState(40)
  const [correctedCount, setCorrectedCount] = useState(teaserCount)
  const [correctedCurrent, setCorrectedCurrent] = useState(current ? current : 1)
  const [roundedSlidesPerView, setRoundedSlidesPerView] = useState(1)

  const updateCountAndCurrent = useCallback(
    (slider) => {
      const current = slider.track.details.rel
      const newRoundedSlidesPerView = Math.round(slider.options.slides.perView)

      setCorrectedCount(Math.ceil(teaserCount / newRoundedSlidesPerView))

      if (newRoundedSlidesPerView === 1 || current === 1) {
        setCorrectedCurrent(current + 1)
      } else {
        setCorrectedCurrent(Math.round(current / newRoundedSlidesPerView) + 1)
      }

      setRoundedSlidesPerView(newRoundedSlidesPerView)

      if (onSliderCreated) {
        onSliderCreated(slider)
      }
    },
    [teaserCount, onSliderCreated]
  )

  const [sliderRef, slider] = useKeenSlider({
    ...sliderSettings,
    slideChanged: (slider) => {
      const current = slider.track.details.rel

      updateCountAndCurrent(slider)

      const currentSlide = slideElementRefs.current[current]
      const currentSlidePicture = currentSlide.querySelector('.sliderElement figure')
        ? // child picture element wrapped in <figure> if image has copyright
          currentSlide.querySelector('.sliderElement figure')
        : // image without copyright
          currentSlide.querySelector('.sliderElement picture')

      if (currentSlidePicture) {
        const pictureHeight = currentSlidePicture.scrollHeight
        setPaginationOffset(pictureHeight)
      }

      changeCallback(current)

      // get height of new slide and reset slider container height to it
      if (sliderSettings.adaptiveHeight && !initialSlideChange.current) {
        setSliderContainerHeight(current, 1)
      }

      initialSlideChange.current = false
    },
    created: updateCountAndCurrent,
    updated: updateCountAndCurrent,
  })

  const handleSlideChange = useCallback(
    (index) => {
      if (roundedSlidesPerView === 1 || index === 1) {
        slider.current.moveToIdx(index - 1)
      } else {
        let newIndex = index * roundedSlidesPerView - (roundedSlidesPerView - 1) - 1
        if (newIndex > teaserCount - roundedSlidesPerView) {
          newIndex = teaserCount - roundedSlidesPerView
        }

        slider.current.moveToIdx(newIndex)
      }
    },
    [roundedSlidesPerView, slider, teaserCount]
  )

  const setSliderContainerHeight = useCallback(
    (slideIndex, slidesPerView) => {
      let slideHeights = []
      let newSlideHeight = 0

      for (let i = 0; i < Math.ceil(slidesPerView); i++) {
        let newSlideHeight =
          slideElementRefs.current[slideIndex + i]?.getElementsByClassName('sliderElement')[0].scrollHeight
        slideHeights.push(newSlideHeight)
      }

      newSlideHeight = Math.max(...slideHeights)

      if (slider?.current?.container) {
        const container = slider.current.container
        container.style.height = `${newSlideHeight}px`
      }
    },
    [slider]
  )

  const handleWindowResize = useCallback(() => {
    resizeCallback()
    if (allowPaginationOffset) {
      if (window.innerWidth >= MOBILE_BREAKPOINT) {
        setPaginationOffsetModifier(48)
      } else {
        setPaginationOffsetModifier(40)
      }
    } else {
      setPaginationOffsetModifier(0)
    }
  }, [resizeCallback, allowPaginationOffset])

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [handleWindowResize, sliderSettings])

  useEffect(() => {
    handleSlideChange(current + 1)
  }, [current, handleSlideChange])

  const onLoadComponent = () => {
    resizeCallback()
    if (sliderSettings.adaptiveHeight) {
      // set initial slider container height to enable smooth slide transition
      setSliderContainerHeight(0, 1)
    }

    if (allowPaginationOffset) {
      if (window.innerWidth >= MOBILE_BREAKPOINT) {
        setPaginationOffsetModifier(48)
      } else {
        setPaginationOffsetModifier(40)
      }
    } else {
      setPaginationOffsetModifier(0)
    }

    const currentSlide = slideElementRefs.current[0]
    const currentSlidePicture = currentSlide.querySelector('.sliderElement figure')
      ? // child picture element wrapped in <figure> if image has copyright
        currentSlide.querySelector('.sliderElement figure')
      : // image without copyright
        currentSlide.querySelector('.sliderElement picture')

    if (currentSlidePicture) {
      const pictureHeight = currentSlidePicture.scrollHeight
      setPaginationOffset(pictureHeight)
    }
  }

  return (
    <div className={sliderClassName}>
      <div
        onLoad={onLoadComponent}
        className="keen-slider transition-all duration-300 ease-out flex items-start"
        style={{ overflow: 'visible' }}
        ref={sliderRef}
      >
        {children.map((element, index) => (
          <div
            key={index}
            ref={(el) => (slideElementRefs.current[index] = el)}
            className={`keen-slider__slide number-slide${index}
              ${slideClassName}
            `}
          >
            <div className="sliderElement">{element}</div>
          </div>
        ))}
      </div>

      {correctedCount > 1 && (
        <div className={`slider-pagination ${paginationOuterClasses}`}>
          <div
            className={paginationClasses}
            style={{ bottom: `${paginationOffset + (allowPaginationOffset ? paginationOffsetModifier : '')}px` }}
          >
            {correctedCount <= 3 ? (
              <DottedPagination
                count={correctedCount}
                current={correctedCurrent}
                onChangeCallback={handleSlideChange}
                variant={paginationSettings.colorScheme}
                mode={sliderSettings.loop ? 'loop' : 'default'}
              />
            ) : (
              <NumberedPaginationShort
                count={correctedCount}
                current={correctedCurrent}
                onChangeCallback={handleSlideChange}
                variant={paginationSettings.colorScheme}
                mode={sliderSettings.loop ? 'loop' : 'default'}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

Slider.propTypes = {
  changeCallback: PropTypes.func,
  children: PropTypes.node,
  current: PropTypes.number,
  teaserCount: PropTypes.number,
  sliderSettings: PropTypes.shape({
    initial: PropTypes.number,
    loop: PropTypes.bool,
    duration: PropTypes.number,
    resetSlide: PropTypes.bool,
    slidesPerView: PropTypes.number,
    spacing: PropTypes.number,
    breakpoints: PropTypes.object,
    slideChanged: PropTypes.func,
    move: PropTypes.func,
    adaptiveHeight: PropTypes.bool,
  }),
  paginationClasses: PropTypes.string,
  paginationOuterClasses: PropTypes.string,
  paginationSettings: PropTypes.shape({
    hideLeftButton: PropTypes.func,
    hideRightButton: PropTypes.func,
    hideDots: PropTypes.func,
    clickableDots: PropTypes.bool,
    colorScheme: PropTypes.oneOf(['light', 'dark']),
  }),
  onChangeSlidesPerView: PropTypes.func,
  resizeCallback: PropTypes.func,
  sliderClassName: PropTypes.string,
  slideClassName: PropTypes.string,
  allowPaginationOffset: PropTypes.bool,
  onSliderCreated: PropTypes.func,
}

Slider.defaultProps = {
  changeCallback: () => {},
  teaserCount: 0,
  paginationClasses: '',
  paginationOuterClasses: '',
  resizeCallback: () => {},
  sliderSettings: {
    loop: false,
    slides: {
      perView: 1,
    },
    centered: true,
    adaptiveHeight: false,
  },
  sliderClassName: '',
  slideClassName: '',
  allowPaginationOffset: true,
}

export default Slider
